/**
 * PageSheet — the web form of the app's iOS `presentationStyle="pageSheet"`
 * modals (edit item, manual entry, edit log entry): a tall sheet rising from
 * the bottom at phone width, a centered ≤600px card on wider screens. The
 * header and (optional sticky) footer stay put while the body scrolls.
 */

import { useEffect, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

export function PageSheet({
  open,
  onClose,
  header,
  footer,
  children,
  label,
  background = 'card',
}: {
  open: boolean
  onClose: () => void
  header?: ReactNode
  footer?: ReactNode
  children: ReactNode
  label?: string
  /** Sheet surface: `card` (edit sheets) or `bg` (screens shown as a sheet). */
  background?: 'card' | 'bg'
}) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [open, onClose])

  if (!open) return null
  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-end justify-center md:items-center md:p-lg" role="dialog" aria-modal="true" aria-label={label}>
      <div className="absolute inset-0 bg-black/50 animate-fade-in" onClick={onClose} />
      <div
        className={
          'relative flex h-[94dvh] w-full max-w-content animate-slide-up flex-col leading-[normal] overflow-hidden rounded-t-[12px] md:h-auto md:max-h-[90vh] md:rounded-xl md:shadow-xl ' +
          (background === 'card' ? 'bg-card' : 'bg-bg')
        }
      >
        {header}
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">{children}</div>
        {footer}
      </div>
    </div>,
    document.body,
  )
}

export default PageSheet
